import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Send, Phone, PhoneOff, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
// STUN für direkte Verbindungen + TURN-Relays als Fallback. Die tatsächliche
// Konfiguration kommt vom Backend (/exams/rtc-config, dort per .env erweiterbar).
const RTC_CONFIG_FALLBACK = {
  iceServers: [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
    {
      urls: [
        'turn:openrelay.metered.ca:80',
        'turn:openrelay.metered.ca:443',
        'turn:openrelay.metered.ca:443?transport=tcp',
        'turns:openrelay.metered.ca:443?transport=tcp',
      ],
      username: 'openrelayproject',
      credential: 'openrelayproject',
    },
  ],
};
const CONNECT_TIMEOUT_MS = 25000;
const MIC_CONSTRAINTS = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } };

// Chat + Sprachverbindung (WebRTC-Audio) zwischen Dozent (admin) und Prüfling (user).
// role='user': schwebender Chat-Button im Prüfungsraum, eingehende Anrufe werden automatisch angenommen.
// role='admin': kompakte Leiste in der Monitor-Kachel mit Chat-Panel + "Sprechen"-Button.
export const ExamChatVoice = ({ attemptId, getAuthHeader, role, userName, getCameraStream, onRemoteStream, portalTarget }) => {
  const base = role === 'admin' ? `${API}/admin/exam-attempts/${attemptId}` : `${API}/user/exam-attempts/${attemptId}`;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [unread, setUnread] = useState(0);
  const [callState, setCallState] = useState('idle'); // idle | connecting | live
  const [needsAudioTap, setNeedsAudioTap] = useState(false);
  const lastTsRef = useRef('');
  const openRef = useRef(false);
  openRef.current = open;
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const connectTimerRef = useRef(null);
  const audioRef = useRef(null);
  const rtcConfigRef = useRef(RTC_CONFIG_FALLBACK);
  const pendingIceRef = useRef([]);
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;
  const scrollRef = useRef(null);

  // ICE-Server-Konfiguration vom Backend laden (per .env erweiterbar)
  useEffect(() => {
    axios.get(`${API}/exams/rtc-config`, authRef.current())
      .then((r) => { if (r.data?.iceServers) rtcConfigRef.current = { iceServers: r.data.iceServers }; })
      .catch(() => {});
  }, []);

  const teardownCall = useCallback(() => {
    if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch { /* noop */ } pcRef.current = null; }
    // Nur das eigene Mikrofon stoppen — der Kamera-Stream gehört dem Prüfungsraum
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach((t) => t.stop()); localStreamRef.current = null; }
    onRemoteStream?.(null);
    setCallState('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendSignal = useCallback((type, payload = '') => {
    return axios.post(`${base}/rtc`, { type, payload }, authRef.current()).catch(() => {});
  }, [base]);

  const buildPc = useCallback((tracks) => {
    const pc = new RTCPeerConnection(rtcConfigRef.current);
    pendingIceRef.current = [];
    tracks.forEach((t) => pc.addTrack(t.track, t.stream));
    pc.onicecandidate = (e) => { if (e.candidate) sendSignal('ice', JSON.stringify(e.candidate)); };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (stream && stream.getVideoTracks().length > 0) {
        onRemoteStream?.(stream);
        if (audioRef.current) audioRef.current.srcObject = null;
      } else if (audioRef.current && !(onRemoteStream && stream?.getVideoTracks().length)) {
        audioRef.current.srcObject = stream;
        audioRef.current.play().then(() => setNeedsAudioTap(false)).catch(() => setNeedsAudioTap(true));
      }
    };
    // 'live' erst, wenn wirklich Daten fließen — nicht schon beim Signaling
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null; }
        setCallState('live');
        // Bandbreite begrenzen, damit Video über schwache Relays flüssig bleibt
        pc.getSenders().forEach((s) => {
          if (s.track?.kind === 'video') {
            const p = s.getParameters();
            p.degradationPreference = 'maintain-framerate';
            p.encodings = [{ ...(p.encodings?.[0] || {}), maxBitrate: 350000, maxFramerate: 15 }];
            s.setParameters(p).catch(() => {});
          }
        });
      } else if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
        if (pc.connectionState === 'failed' && role === 'admin') {
          toast.error('Video-/Tonverbindung fehlgeschlagen (Netzwerk blockiert die Übertragung). Bitte den Chat nutzen oder erneut versuchen.');
        }
        teardownCall();
      }
    };
    // Timeout: wenn nach 25s keine Verbindung steht, sauber abbrechen
    if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    connectTimerRef.current = setTimeout(() => {
      if (pcRef.current === pc && pc.connectionState !== 'connected') {
        if (role === 'admin') toast.error('Keine Verbindung zum Prüfling möglich — bitte den Chat nutzen.');
        sendSignal('bye');
        teardownCall();
      }
    }, CONNECT_TIMEOUT_MS);
    pcRef.current = pc;
    return pc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendSignal, teardownCall]);

  const flushPendingIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    const queued = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const cand of queued) {
      await pc.addIceCandidate(cand).catch(() => {});
    }
  }, []);

  const handleSignals = useCallback(async (signals) => {
    for (const s of signals) {
      try {
        if (s.type === 'offer') {
          // Eingehender Anruf (Prüfling): Mikrofon + Kamerabild senden, automatisch annehmen
          const mic = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
          localStreamRef.current = mic;
          const cam = getCameraStream?.();
          const outStream = new MediaStream([
            ...mic.getAudioTracks(),
            ...(cam ? cam.getVideoTracks() : []),
          ]);
          const pc = buildPc(outStream.getTracks().map((t) => ({ track: t, stream: outStream })));
          setCallState('connecting');
          await pc.setRemoteDescription(JSON.parse(s.payload));
          await flushPendingIce();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendSignal('answer', JSON.stringify(answer));
          toast.info('Die Aufsicht sieht und hört dich jetzt — du hörst sie über die Lautsprecher.');
        } else if (s.type === 'answer' && pcRef.current) {
          await pcRef.current.setRemoteDescription(JSON.parse(s.payload));
          await flushPendingIce();
        } else if (s.type === 'ice') {
          const cand = JSON.parse(s.payload);
          if (pcRef.current && pcRef.current.remoteDescription) {
            await pcRef.current.addIceCandidate(cand).catch(() => {});
          } else {
            // Kandidat kam vor der Session-Beschreibung an → puffern statt verlieren
            pendingIceRef.current.push(cand);
          }
        } else if (s.type === 'bye') {
          teardownCall();
        }
      } catch (err) {
        console.error('RTC signal error', err);
      }
    }
  }, [buildPc, sendSignal, teardownCall, flushPendingIce]);

  // Polling: Chat + Signale
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const [chatRes, rtcRes] = await Promise.all([
          axios.get(`${base}/chat?since=${encodeURIComponent(lastTsRef.current)}`, authRef.current()),
          axios.get(`${base}/rtc`, authRef.current()),
        ]);
        if (!alive) return;
        const msgs = chatRes.data.messages || [];
        if (msgs.length) {
          lastTsRef.current = msgs[msgs.length - 1].at;
          setMessages((m) => [...m, ...msgs.filter((x) => !m.some((y) => y.at === x.at && y.text === x.text))]);
          const incoming = msgs.filter((x) => x.from_role !== role).length;
          if (incoming && !openRef.current) setUnread((u) => u + incoming);
        }
        const signals = rtcRes.data.signals || [];
        if (signals.length) await handleSignals(signals);
      } catch { /* noop */ }
    };
    poll();
    const t = setInterval(poll, callState === 'idle' ? 4000 : 1500);
    return () => { alive = false; clearInterval(t); };
  }, [base, role, callState, handleSignals]);

  useEffect(() => () => {
    if (pcRef.current) { sendSignal('bye'); }
    teardownCall();
  }, [sendSignal, teardownCall]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    try {
      const r = await axios.post(`${base}/chat`, { text }, authRef.current());
      setMessages((m) => [...m, r.data.message]);
      lastTsRef.current = r.data.message.at;
    } catch { toast.error('Senden fehlgeschlagen'); }
  };

  const startCall = async () => {
    try {
      const mic = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      localStreamRef.current = mic;
      const pc = buildPc(mic.getTracks().map((t) => ({ track: t, stream: mic })));
      pc.addTransceiver('video', { direction: 'recvonly' });
      setCallState('connecting');
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
      await pc.setLocalDescription(offer);
      sendSignal('offer', JSON.stringify(offer));
    } catch {
      toast.error('Mikrofonzugriff fehlgeschlagen');
      teardownCall();
    }
  };

  const endCall = () => { sendSignal('bye'); teardownCall(); };

  const chatPanel = (
    <div className={`flex flex-col bg-cream-50 border border-cream-300 rounded-2xl shadow-xl overflow-hidden ${role === 'user' ? 'w-[320px] h-[400px]' : 'w-full h-[280px]'}`} data-testid="exam-chat-panel">
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-espresso text-cream-50">
        <p className="text-[12px] font-semibold">{role === 'user' ? 'Frage an die Dozentin / den Dozenten' : `Chat mit ${userName || 'Prüfling'}`}</p>
        <button onClick={() => setOpen(false)} data-testid="exam-chat-close"><X className="w-4 h-4" /></button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2.5 space-y-2">
        {messages.length === 0 && (
          <p className="text-[12px] text-sand-500 italic pt-2">
            {role === 'user' ? 'Wenn du eine Verständnisfrage hast, schreib sie hier — die Aufsicht liest mit.' : 'Noch keine Nachrichten.'}
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] px-3 py-2 rounded-xl text-[12.5px] leading-snug ${m.from_role === role ? 'ml-auto bg-terra text-cream-50' : 'bg-cream-200 text-sand-800'}`}>
            {m.text}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 p-2.5 border-t border-cream-300">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Nachricht…"
          className="flex-1 px-3 py-2 bg-cream-100 border border-cream-300 rounded-full text-[12.5px] focus:outline-none focus:border-terra"
          data-testid="exam-chat-input"
        />
        <button onClick={send} className="p-2 rounded-full bg-terra text-cream-50" data-testid="exam-chat-send"><Send className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );

  if (role === 'admin') {
    const controls = (
      <div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => { setOpen((o) => !o); setUnread(0); }}
            className="relative flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border border-cream-400 text-sand-700 hover:bg-cream-200"
            data-testid={`monitor-chat-btn-${attemptId}`}
          >
            <MessageCircle className="w-3 h-3" /> Chat
            {unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[9px] px-1.5 rounded-full">{unread}</span>}
          </button>
          {callState === 'idle' ? (
            <button onClick={startCall} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: '#2F7A3D', color: '#FBF7F0' }} data-testid={`monitor-call-btn-${attemptId}`}>
              <Phone className="w-3 h-3" /> Video & Sprechen
            </button>
          ) : (
            <button onClick={endCall} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white" data-testid={`monitor-hangup-btn-${attemptId}`}>
              {callState === 'connecting' ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneOff className="w-3 h-3" />}
              {callState === 'connecting' ? 'Verbinde…' : 'Auflegen'}
            </button>
          )}
          {callState === 'live' && (
            <span className="flex items-center gap-1 text-[10px] font-bold text-green-800 uppercase tracking-wider" data-testid={`monitor-live-indicator-${attemptId}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-600 animate-pulse" /> Ton verbunden
            </span>
          )}
        </div>
        {open && <div className="mt-2">{chatPanel}</div>}
      </div>
    );
    return (
      <div>
        <audio ref={audioRef} autoPlay />
        {portalTarget ? createPortal(controls, portalTarget) : controls}
      </div>
    );
  }

  // role === 'user': schwebend unten rechts
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      <audio ref={audioRef} autoPlay />
      {callState !== 'idle' && (
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-espresso text-cream-50 text-[12px] shadow-lg" data-testid="exam-voice-indicator">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          {callState === 'connecting' ? 'Verbindung zur Aufsicht wird aufgebaut…' : 'Die Aufsicht sieht und hört dich gerade (Video & Ton)'}
        </div>
      )}
      {needsAudioTap && callState === 'live' && (
        <button
          onClick={() => { audioRef.current?.play().then(() => setNeedsAudioTap(false)).catch(() => {}); }}
          className="px-3.5 py-2 rounded-full bg-terra text-cream-50 text-[12px] font-bold shadow-lg animate-pulse"
          data-testid="exam-audio-tap-btn"
        >
          🔊 Hier klicken, um die Aufsicht zu hören
        </button>
      )}
      {open && chatPanel}
      <button
        onClick={() => { setOpen((o) => !o); setUnread(0); }}
        className="relative w-12 h-12 rounded-full bg-espresso text-cream-50 shadow-lg flex items-center justify-center"
        data-testid="exam-chat-toggle"
        aria-label="Chat mit der Aufsicht"
      >
        <MessageCircle className="w-5 h-5" />
        {unread > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full" data-testid="exam-chat-unread">{unread}</span>}
      </button>
    </div>
  );
};

export default ExamChatVoice;
